import React, { useState, useEffect, useRef } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
/* global axios */
import api from '../services/apiService';
import { useParams, useNavigate } from 'react-router-dom';
import he from 'he';

const EmailTemplateEditor = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const quillRef = useRef(null);

  // Editor und Formular States
  const [editorContent, setEditorContent] = useState('<p><br></p>');
  const [templateName, setTemplateName] = useState('');
  const [subject, setSubject] = useState('');
  const [language, setLanguage] = useState('');
  const [showInMessageSelection, setShowInMessageSelection] = useState(true);
  const [placeholdersInput, setPlaceholdersInput] = useState('');
  const [dynamicPlaceholders, setDynamicPlaceholders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({
    id: '',
    signatureImage: '',  // Base64-kodiertes Signaturbild
    // weitere Felder des MessageTemplate-Modells...
  });


  // Vorlage laden, falls eine ID vorhanden ist
  useEffect(() => {
    if (!id) {
      setLoading(false);
      return;
    }
    const fetchTemplate = async () => {
      try {
        const response = await api.get(`/messagetemplates/${id}`);
        const { name, subject, bodyHtml, language, signatureImage, showInMessageSelection } = response.data;
        setTemplateName(name || '');
        setSubject(subject || '');
        setLanguage(language || '');
        setShowInMessageSelection(showInMessageSelection !== false);
        // HTML unescape: Falls der gespeicherte bodyHtml escapen ist, decodieren wir ihn
        const decodedHtml = he.decode(bodyHtml || '');
        setEditorContent(decodedHtml.trim() !== '' ? decodedHtml : '<p><br></p>');
        setFormData(prev => ({ ...prev, signatureImage: signatureImage || '' }));
        setLoading(false);
      } catch (error) {
        console.error("Fehler beim Laden der Vorlage:", error);
        setLoading(false);
      }
    };
    fetchTemplate();
  }, [id]);

  // Dynamische Platzhalter laden
  useEffect(() => {
    const fetchDynamicPlaceholders = async () => {
      try {
        const response = await api.get('/placeholders');
        // Erwartete Struktur: { customerPlaceholders: [...], contactPlaceholders: [...] }
        const customerPlaceholders = response.data.customerPlaceholders || [];
        const contactPlaceholders = response.data.contactPlaceholders || [];
        setDynamicPlaceholders([...customerPlaceholders, ...contactPlaceholders]);
      } catch (error) {
        console.error("Fehler beim Laden der Platzhalter:", error);
      }
    };
    fetchDynamicPlaceholders();
  }, []);

  // Platzhalter in den Editor einfügen
  const insertPlaceholder = (placeholder) => {
    if (!placeholder) return;
    const editor = quillRef.current.getEditor();
    const range = editor.getSelection();
    if (range) {
      editor.insertText(range.index, placeholder);
    }
  };

  // onChange-Handler: Aktualisiere den Editorinhalt im State
  const handleEditorChange = (content, delta, source, editor) => {
    setEditorContent(content);
  };

   // Gemeinsame Funktion, um ausgewählte Bilder in Base64 umzuwandeln
   const handleFileChange = (e, fieldName) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onloadend = () => {
      setFormData(prev => ({ ...prev, [fieldName]: reader.result }));
    };
    reader.readAsDataURL(file);
  };

  // Speichern / Aktualisieren der Vorlage
  const handleSave = async () => {
    if (!templateName || !subject) {
      alert("Bitte alle Pflichtfelder ausfüllen!");
      return;
    }
    try {
      const editor = quillRef.current.getEditor();
      // Delta aus dem Editor
      const delta = editor.getContents() || { ops: [{ insert: "\n" }] };
      // Verwende den Inhalt, der über onChange im State gespeichert wurde
      const html = editorContent && editorContent.trim() !== '' ? editorContent : '<p><br></p>';

      const payload = {
        name: templateName,
        subject,
        language: language || null,
        placeholders: placeholdersInput
          ? placeholdersInput.split(',').map(p => p.trim())
          : [],
        bodyDelta: delta,
        bodyHtml: html,
        signatureImage: Array.isArray(formData.signatureImage) ? '' : formData.signatureImage,
        showInMessageSelection
      };

      console.log("Payload:", payload);

      if (id) {
        await api.put(`/messagetemplates/${id}`, payload);
        alert("Vorlage erfolgreich aktualisiert!");
      } else {
        await api.post("/messagetemplates", payload);
        alert("Vorlage erfolgreich erstellt!");
      }
      navigate('/messagetemplatelist');
    } catch (error) {
      console.error("Fehler beim Speichern:", error);
      alert("Fehler beim Speichern!");
    }
  };

  if (loading) {
    return <div>Daten werden geladen...</div>;
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', marginBottom: '20px', maxWidth: '600px', marginLeft: '645px' }}>
      <h2>{id ? "Vorlage bearbeiten" : "Neue Vorlage erstellen"}</h2>
     
      <input
        type="text"
        value={templateName}
        onChange={(e) => setTemplateName(e.target.value)}
        placeholder="Vorlagenname"
      />
      <input
        type="text"
        value={subject}
        onChange={(e) => setSubject(e.target.value)}
        placeholder="Betreff"
      />
      <input
        type="text"
        value={language}
        onChange={(e) => setLanguage(e.target.value)}
        placeholder="Sprache (optional)"
      />
      <label style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', gap: '4px', width: '100%', textAlign: 'left', marginTop: '10px' }}>
        <input
          type="checkbox"
          checked={showInMessageSelection}
          onChange={(e) => setShowInMessageSelection(e.target.checked)}
          style={{ width: 'auto', margin: 0 }}
        />
        In Nachrichten-Vorlagenauswahl anzeigen
      </label>


       {/* Neuer Upload-Bereich für das Signaturbild */}
       <div className="upload-section" style={{ marginTop: '20px' }}>
        <label htmlFor="signatureImage">alternativ Signatur ( 300x60 px ):</label>
        <input
          type="file"
          id="signatureImage"
          accept="image/*"
          onChange={(e) => handleFileChange(e, 'signatureImage')}
        />
        {formData.signatureImage && (
          <img src={formData.signatureImage} alt="Vorschau Signaturbild" style={{ maxWidth: '200px', marginTop: '10px' }} />
        )}
      </div>

      

      <ReactQuill
        ref={quillRef}
        value={editorContent}
        onChange={handleEditorChange}
        modules={{
          toolbar: [
            [{ header: [1, 2, 3, false] }],
            
            ['bold', 'italic', 'underline', 'strike'],
            [{ list: 'ordered' }, { list: 'bullet' }],
            ['link'],
            ['clean']
          ]
        }}
        formats={[
          'header', 'bold', 'italic', 'underline', 'strike', 'list', 'bullet', 'link'
        ]}
        style={{ 
          height: '400px', 
          marginTop: '10px', 
          marginBottom: '0px',
          backgroundColor: '#fff' // Hintergrundfarbe weiß
        }}
      />

      <button onClick={handleSave} style={{ marginTop: '60px' }}>
        {id ? "Speichern" : "Erstellen"}
      </button>
    </div>
  );
};

export default EmailTemplateEditor;
